import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { wishlistAPI, activityAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { getActivitySessionId } from '../utils/activitySession';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) throw new Error('useWishlist must be used within a WishlistProvider');
  return context;
};

const LS_KEY = 'wishlist';

const loadFromLS = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch {
    return [];
  }
};

const saveToLS = (items) => {
  localStorage.setItem(LS_KEY, JSON.stringify(items));
};

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [wishlistItems, setWishlistItems] = useState([]);

  // Derive product id set for fast lookup
  const wishlistIds = new Set(wishlistItems.map((item) => item.id));

  // Load wishlist: backend (logged in) or localStorage (guest)
  useEffect(() => {
    const loadWishlist = async () => {
      if (user?.id) {
        try {
          // Try to sync localStorage items first, then load from backend
          const localIds = loadFromLS().map((i) => (typeof i === 'object' ? i.id : i)).filter(Boolean);
          if (localIds.length > 0) {
            await wishlistAPI.sync(user.id, localIds);
            localStorage.removeItem(LS_KEY);
          }
          const res = await wishlistAPI.getProducts(user.id);
          const products = res.data?.data?.products || [];
          setWishlistItems(products.map((p) => ({
            id: p.id,
            name: p.name,
            price: p.price,
            image: p.imageUrl,
            stockQuantity: p.stockQuantity,
            isActive: p.isActive,
            addedAt: p.addedAt,
          })));
        } catch {
          // Fallback to localStorage on network error
          setWishlistItems(loadFromLS());
        }
      } else {
        setWishlistItems(loadFromLS());
      }
    };

    loadWishlist();
  }, [user?.id]);

  const addToWishlist = useCallback(async (product) => {
    if (wishlistIds.has(product.id)) return;

    const newItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.mainImageUrl || product.imageUrl || product.image,
      category: product.category,
      stockQuantity: product.stockQuantity,
      addedAt: new Date().toISOString(),
    };

    setWishlistItems((prev) => [...prev, newItem]);

    // Müşteri tercih takibi: favoriye ekleme sinyali
    activityAPI.track(getActivitySessionId(), product.id, 'WISHLIST').catch(() => {});

    if (user?.id) {
      try {
        await wishlistAPI.add(user.id, product.id);
      } catch {
        // Revert on failure
        setWishlistItems((prev) => prev.filter((i) => i.id !== product.id));
      }
    } else {
      saveToLS([...wishlistItems, newItem]);
    }
  }, [user?.id, wishlistIds, wishlistItems]);

  const removeFromWishlist = useCallback(async (productId) => {
    const prev = [...wishlistItems];
    setWishlistItems((items) => items.filter((i) => i.id !== productId));

    if (user?.id) {
      try {
        await wishlistAPI.remove(user.id, productId);
      } catch {
        setWishlistItems(prev); // Revert
      }
    } else {
      saveToLS(wishlistItems.filter((i) => i.id !== productId));
    }
  }, [user?.id, wishlistItems]);

  const isInWishlist = useCallback((productId) => wishlistIds.has(productId), [wishlistIds]);

  const toggleWishlist = useCallback((product) => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  }, [isInWishlist, addToWishlist, removeFromWishlist]);

  const clearWishlist = useCallback(() => {
    setWishlistItems([]);
    saveToLS([]);
  }, []);

  const getWishlistCount = () => wishlistItems.length;

  const getWishlistTotal = () =>
    wishlistItems.reduce((total, item) => total + (item.price || 0), 0);

  return (
    <WishlistContext.Provider value={{
      wishlistItems,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      toggleWishlist,
      clearWishlist,
      getWishlistCount,
      getWishlistTotal,
    }}>
      {children}
    </WishlistContext.Provider>
  );
};
